import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import RepExample from "@/components/RepExample";
import PageMeta from "@/components/PageMeta";
import { SITE, has, listJoin, ratesValid, lateFeeText } from "@/config/site";
import { CaretDown } from "@phosphor-icons/react";

/**
 * Builds the FAQ groups from config, hiding any Q&A whose required fact is
 * missing. Returned shape: [{ title, items: [{ q, a (ReactNode) }] }]
 */
function useFaqGroups() {
  const lending = SITE.LENDING_ENABLED;
  const groups = [];

  if (lending) {
    const items = [];
    items.push({
      q: "How do school-fee instalments work?",
      a: `You apply online, and if approved, we pay your child's school the full fee straight away. You then repay us in equal monthly payments over ${SITE.TENORS_MONTHS.join(", ")} months. Your child starts the term on time, and the school never needs to know how you paid.`,
    });
    if (has(SITE.ELIGIBILITY_TEXT)) items.push({ q: "Who can apply?", a: SITE.ELIGIBILITY_TEXT });
    if (has(SITE.DOCUMENTS_NEEDED_TEXT)) items.push({ q: "What do I need to apply?", a: SITE.DOCUMENTS_NEEDED_TEXT });
    if (has(SITE.DECISION_TIME)) {
      items.push({
        q: "How quickly will I get a decision?",
        a: `Most parents hear back within ${SITE.DECISION_TIME}. If we need more information, we'll tell you exactly what and why.`,
      });
    }
    if (ratesValid()) items.push({
      q: "How much does it cost?",
      a: (
        <>
          Interest is between <strong>{SITE.RATE_MIN_PA}%</strong> and{" "}
          <strong>{SITE.RATE_MAX_PA}%</strong> a year, depending on your assessment. Before you accept, we show
          your monthly payment, the total you'll repay and the exact cost of borrowing in naira.
          {has(SITE.APPLICATION_FEE_PCT) && (
            <> There is a one-time processing fee of {SITE.APPLICATION_FEE_PCT}% of the amount you borrow, added to your loan and included in the total you repay. The school always receives the full fee.</>
          )}{" "}
          <Link to="/pricing" className="text-[#0A3622] underline">See the cost table</Link>.
        </>
      ),
    });
    items.push({
      q: "Why might my rate differ from someone else's?",
      a: "Your rate depends on our affordability assessment, including your income and repayment history. Whatever rate we offer, you see it, and the total cost, before you decide.",
    });
    items.push({
      q: "How much can I borrow?",
      a: "We only lend what our checks say you can comfortably repay, so we may offer a smaller amount or a longer plan.",
    });
    items.push({
      q: "Can I use instalments for more than one child?",
      a: "Yes. Each child's fee is a separate plan, and we look at all your plans together to make sure the total stays affordable.",
    });
    if (SITE.SOFT_CHECK_AVAILABLE) {
      items.push({
        q: "Will checking my eligibility affect my credit record?",
        a: "No. Checking what you can borrow is a soft check. A full credit check happens only if you go ahead and apply.",
      });
    }
    items.push({
      q: "What do you check when I apply?",
      a: "We check your identity, income and credit history. We never ask for access to your phone contacts, photos or messages.",
    });
    items.push({
      q: "How do repayments work?",
      a: `${SITE.REPAYMENT_METHOD_TEXT}. We send an SMS reminder ${SITE.REMINDER_DAYS_BEFORE} days before.`,
    });
    if (!SITE.EARLY_REPAYMENT_PENALTY) {
      items.push({
        q: "Can I pay off my plan early?",
        a: "Yes, at any time, with no penalty. You pay interest only for the time you borrowed, so paying early saves you money.",
      });
    }
    if (has(SITE.LATE_FEE_PCT)) {
      items.push({
        q: "What happens if I miss a payment?",
        a: `A late fee of ${lateFeeText()} We contact you to agree a way forward. We never contact your family, friends, employer or phone contacts, and we never shame you publicly.`,
      });
    }
    if (has(SITE.HARDSHIP_OPTIONS)) {
      items.push({
        q: "I'm struggling to repay. What should I do?",
        a: `Call or WhatsApp us as early as you can, ideally before the due date. Depending on your situation, we may be able to ${listJoin(SITE.HARDSHIP_OPTIONS, "or")}. Asking for help will never count against you.`,
      });
    }
    items.push({
      q: "Will my child be affected if I miss a payment?",
      a: "No. Your agreement is between you and Ascentremit. The school has already been paid in full, and we never ask a school to send a pupil home, withhold results or treat them differently.",
    });
    if (has(SITE.CREDIT_BUREAU)) {
      items.push({
        q: "Does repaying on time help my credit history?",
        a: `Yes. We report repayments to ${SITE.CREDIT_BUREAU}, so a plan repaid on time can help you borrow on better terms in future. Missed payments are reported too.`,
      });
    }
    items.push({
      q: "Can I use instalments again next term?",
      a: "Yes. Parents who repay on time can apply again.",
    });
    if (has(SITE.LENDING_ENTITY)) {
      items.push({
        q: "Who provides the loan?",
        a: `Instalment plans are loans from ${SITE.LENDING_ENTITY}.`,
      });
    }
    groups.push({ title: "Paying school fees in instalments", id: "instalments", items });
  }

  // Group 2 — Paying the full fee
  {
    const items = [
      {
        q: "How do I pay in full?",
        a: "Open a free account, find your child's school, and pay by card, bank transfer or USSD. If you live abroad, you can pay in US dollars by card.",
      },
      {
        q: "My child's school isn't listed. What can I do?",
        a: "Tell us the school's name and we'll contact them. It's free for them to join, and they'll be able to offer you instalments too.",
      },
    ];
    if (has(SITE.SCHOOL_SETTLEMENT_TIME)) {
      items.push({
        q: "How do I know the school received my payment?",
        a: `You get a receipt by email and SMS straight away. The payment also shows on the school's dashboard against your child's name, and money reaches the school's bank account ${SITE.SCHOOL_SETTLEMENT_TIME}.`,
      });
    }
    items.push({
      q: "Is it safe to pay by card?",
      a: "Yes. Card payments are handled by Paystack and Stripe. Ascentremit never sees or stores your full card number.",
    });
    if (has(SITE.REFUND_DAYS)) {
      items.push({
        q: "Can I get a refund?",
        a: `Refunds of school fees follow the school's own refund policy. If the school approves a refund on a fee paid by instalments, it goes towards your loan balance first, and any extra is returned to you. Approved refunds reach your original payment method within ${SITE.REFUND_DAYS} working days. If we charged you in error, we refund you in full.`,
      });
    }
    items.push({
      q: "Do you charge parents to pay in full?",
      a: "No. Paystack or Stripe add a small processing charge, which we show before you confirm.",
    });
    groups.push({ title: "Paying the full fee", id: "full-fee", items });
  }

  // Group 3 — Wallet (only if partner bank is set)
  if (has(SITE.WALLET_PARTNER_BANK)) {
    const items = [
      {
        q: "What is the Ascentremit wallet?",
        a: "A place to set money aside for next term's fees, a little at a time. When the bill arrives, you pay straight from your wallet.",
      },
      {
        q: "Where is my wallet money kept?",
        a: `In a dedicated account at ${SITE.WALLET_PARTNER_BANK}, licensed by the Central Bank of Nigeria, kept separate from Ascentremit's own money.`,
      },
    ];
    if (has(SITE.WALLET_RATE_PA)) {
      items.push({
        q: "Does my wallet earn interest?",
        a: `Yes, ${SITE.WALLET_RATE_PA}% a year.`,
      });
    }
    items.push({
      q: "Can I withdraw my savings?",
      a: "Yes, at any time, to your own bank account.",
    });
    groups.push({ title: "The wallet", id: "wallet", items });
  }

  // Group 4 — Data & help
  {
    const items = [];
    if (has(SITE.PRIVACY_EMAIL)) {
      items.push({
        q: "What do you do with my personal information?",
        a: (
          <>
            We use it only to run your account, process payments and assess loans. We never sell it. We follow the
            Nigeria Data Protection Act 2023, and you can ask to see or delete your data by emailing{" "}
            <a href={`mailto:${SITE.PRIVACY_EMAIL}`} className="text-[#0A3622] underline">{SITE.PRIVACY_EMAIL}</a>.{" "}
            Read our <Link to="/privacy" className="text-[#0A3622] underline">Privacy Policy</Link>.
          </>
        ),
      });
    }
    if (has(SITE.COMPANY_LEGAL_NAME) && has(SITE.CAC_RC_NUMBER)) {
      items.push({
        q: "Who is Ascentremit?",
        a: (
          <>
            Ascentremit is run by {SITE.COMPANY_LEGAL_NAME}, {SITE.CAC_RC_NUMBER}. Meet the team on our{" "}
            <Link to="/about" className="text-[#0A3622] underline">About page</Link>.
          </>
        ),
      });
    }
    if (has(SITE.COMPLAINTS_EMAIL)) {
      items.push({
        q: "How do I make a complaint?",
        a: (
          <>
            Email <a href={`mailto:${SITE.COMPLAINTS_EMAIL}`} className="text-[#0A3622] underline">{SITE.COMPLAINTS_EMAIL}</a>{" "}
            or WhatsApp us. We acknowledge every complaint within 24 hours.
            {lending && (
              <> If you're unhappy with how we've handled a loan complaint, you can take it to the Federal
                Competition and Consumer Protection Commission (FCCPC).
              </>
            )}
          </>
        ),
      });
    }
    if (items.length) groups.push({ title: "Your data and getting help", id: "help", items });
  }

  return groups;
}

/* Flatten text for JSON-LD; strip simple ReactNodes to a readable string. */
function nodeToText(node) {
  if (node == null) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join("");
  if (node.props && node.props.children) return nodeToText(node.props.children);
  return "";
}

export default function FAQ() {
  const groups = useFaqGroups();

  // Inject FAQPage structured data
  useEffect(() => {
    const flat = groups.flatMap((g) =>
      g.items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: nodeToText(it.a) },
      })),
    );
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: flat,
    });
    // Idempotent: remove any pre-rendered copy before appending the live one.
    const prev = document.getElementById("faq-jsonld");
    if (prev) prev.remove();
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("faq-jsonld");
      if (existing) existing.remove();
    };
  }, [groups]);

  return (
    <div className="min-h-screen bg-[#F9F7F4]">
      <PageMeta
        title="Help & FAQ — Ascentremit"
        description="Answers for parents on paying school fees in monthly instalments, paying in full, your wallet, your data and getting support."
      />
      <PublicNav />
      <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-16 py-14 lg:py-20">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#132A13] font-medium leading-tight">
          Help centre & FAQ
        </h1>
        <p className="mt-6 text-base sm:text-lg text-[#4F5D4F] max-w-2xl">
          Straight answers. Still stuck? Email {SITE.SUPPORT_EMAIL} or WhatsApp us.
        </p>

        <div className="mt-12 space-y-12">
          {groups.map((g) => (
            <section key={g.id} id={g.id} data-testid={`faq-group-${g.id}`}>
              <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">{g.title}</h2>
              <div className="mt-5 rounded-2xl border border-[#E5E3DB] bg-white overflow-hidden">
                {g.items.map((it, i) => (
                  <Accordion
                    key={it.q}
                    q={it.q}
                    a={it.a}
                    isLast={i === g.items.length - 1}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </section>

      <RepExample />
      <Footer />
    </div>
  );
}

function Accordion({ q, a, isLast }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={isLast ? "" : "border-b border-[#E5E3DB]"}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left py-4 px-4 sm:px-5 flex items-start justify-between gap-4 hover:bg-[#F9F7F4] transition"
        data-testid={`faq-q-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
        aria-expanded={open}
      >
        <span className="font-display text-base sm:text-lg font-medium text-[#132A13] leading-snug">{q}</span>
        <CaretDown
          size={18}
          weight="bold"
          className={`shrink-0 mt-1 text-[#0A3622] transition ${open ? "rotate-180" : ""}`}
        />
      </button>
      {/*
        Answer is ALWAYS in the DOM so pre-rendered HTML carries the full text
        for crawlers. We visually hide it when `open` is false via inline style
        (not Tailwind's `hidden` class) so screen readers can still find it and
        the content survives SPA hydration without a layout jump.
      */}
      <div
        role="region"
        aria-hidden={!open}
        className="px-4 sm:px-5 text-sm text-[#4F5D4F] leading-relaxed"
        style={
          open
            ? { maxHeight: "none", overflow: "visible", paddingBottom: "1.25rem", opacity: 1 }
            : { maxHeight: 0, overflow: "hidden", paddingBottom: 0, opacity: 0 }
        }
      >
        {a}
      </div>
    </div>
  );
}
